import React, { useState } from 'react';
import { calculateWaterRequirement } from '../utils/waterCalculator';
import { predictYield } from '../utils/yieldPredictor';
import { useLanguage } from './Header';
import { useTranslation } from '../utils/translations';

const Services = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const [crop, setCrop] = useState('');
  const [area, setArea] = useState('');
  const [stage, setStage] = useState('mid');
  const [waterResult, setWaterResult] = useState(null);
  const [yieldDays, setYieldDays] = useState('');
  const [yieldStage, setYieldStage] = useState('flowering');
  const [yieldResult, setYieldResult] = useState(null);
  
  const calculateWater = () => {
    if (!crop || !area) {
      console.log('Missing crop or area:', { crop, area });
      return;
    }
    
    try {
      console.log('Calculating water for:', { crop, area, stage });
      const result = calculateWaterRequirement({
        crop,
        areaAcres: parseFloat(area),
        ETo: 5.2, // Default ETo for Bangalore
        stage,
        efficiency: 0.7,
        effectiveRain: 0
      });
      console.log('Water result:', result);
      setWaterResult(result);
    } catch (error) {
      console.error('Water calculation error:', error);
    }
  };
  
  const calculateYield = () => {
    if (!crop || !yieldDays || !area) {
      console.log('Missing data for yield:', { crop, yieldDays, area });
      return;
    }
    
    try {
      console.log('Calculating yield for:', { crop, yieldStage, yieldDays, area });
      const result = predictYield({
        crop,
        stage: yieldStage,
        daysSincePlanting: parseInt(yieldDays),
        areaAcres: parseFloat(area)
      });
      console.log('Yield result:', result);
      setYieldResult(result);
    } catch (error) {
      console.error('Yield calculation error:', error);
    }
  };
  
  const features = [
    {
      title: "Crop Water Prediction",
      description: "AI-powered water requirement forecasting for optimal irrigation.",
      icon: "💧"
    },
    {
      title: "Yield Prediction",
      description: "Predict crop yields based on weather, soil, and farming practices.",
      icon: "🌾"
    },
    {
      title: "Disease Detection",
      description: "Identify plant diseases instantly by uploading leaf images.",
      icon: "🔍"
    },
    {
      title: "Weather Insights",
      description: "Real-time weather data and forecasts for better planning.",
      icon: "☁️"
    },
    {
      title: "Soil Analysis",
      description: "Comprehensive soil health monitoring and recommendations.",
      icon: "🌱"
    },
    {
      title: "Smart Recommendations",
      description: "Personalized farming advice based on your specific conditions.",
      icon: "🧠"
    }
  ];

  return (
    <section id="prediction" className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('cropPrediction')}</h2>
        
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">{t('waterRequirementPrediction')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('cropType')}</label>
                <select 
                  value={crop} 
                  onChange={(e) => setCrop(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent"
                >
                  <option value="">{t('selectCrop')}</option>
                  <option value="rice">{t('rice')}</option>
                  <option value="wheat">{t('wheat')}</option>
                  <option value="tomato">{t('tomato')}</option>
                  <option value="potato">{t('potato')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldSize')} ({t('acres')})</label>
                <input 
                  type="number" 
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent" 
                  placeholder={t('fieldSizePlaceholder')} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('growthStage')}</label>
                <select 
                  value={stage} 
                  onChange={(e) => setStage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent"
                >
                  <option value="initial">{t('initial')}</option>
                  <option value="mid">{t('midSeason')}</option>
                  <option value="late">{t('lateSeason')}</option>
                </select>
              </div>
              <button onClick={calculateWater} className="btn-primary w-full">{t('predictWaterNeed')}</button>
            </div>
            
            {waterResult && (
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <h4 className="font-semibold text-blue-900">{t('waterRequirement')}</h4>
                <p className="text-blue-700">{t('daily')}: {waterResult.dailyLiters.toLocaleString()}L</p>
                <p className="text-blue-700">{t('weekly')}: {waterResult.weeklyLiters.toLocaleString()}L</p>
                <p className="text-sm text-blue-600">{t('bestWateringTime')}</p>
              </div>
            )}
          </div>
          
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">{t('yieldPrediction')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('cropType')}</label>
                <select 
                  value={crop} 
                  onChange={(e) => setCrop(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent"
                >
                  <option value="">{t('selectCrop')}</option>
                  <option value="rice">{t('rice')}</option>
                  <option value="wheat">{t('wheat')}</option>
                  <option value="tomato">{t('tomato')}</option>
                  <option value="potato">{t('potato')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldSize')} ({t('acres')})</label>
                <input 
                  type="number" 
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent" 
                  placeholder={t('fieldSizePlaceholder')} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('growthStage')}</label>
                <select 
                  value={yieldStage}
                  onChange={(e) => setYieldStage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent"
                >
                  <option value="flowering">{t('flowering')}</option>
                  <option value="vegetative">{t('vegetative')}</option>
                  <option value="fruiting">{t('fruiting')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('daysSincePlanting')}</label>
                <input 
                  type="number" 
                  value={yieldDays}
                  onChange={(e) => setYieldDays(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-dark-azure focus:border-transparent" 
                  placeholder={t('daysPlaceholder')} 
                />
              </div>
              <button onClick={calculateYield} className="btn-primary w-full">{t('predictYield')}</button>
            </div>
            
            {yieldResult && (
              <div className="mt-6 p-4 bg-green-50 rounded-xl">
                <h4 className="font-semibold text-green-900">{t('yieldPredictionResult')}</h4>
                <p className="text-green-700">{t('perAcre')}: {yieldResult.projected_kg_per_acre} kg</p>
                {yieldResult.projected_total_kg && (
                  <p className="text-green-700">{t('total')}: {yieldResult.projected_total_kg.toLocaleString()} kg</p>
                )}
                <p className="text-sm text-green-600">{t('confidence')}: {yieldResult.confidence}%</p>
                <p className="text-sm text-green-600">{t('harvestIn')}: {yieldResult.daysToHarvest} {t('days')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;